/**
 * About this product — tablist that shows one carousel panel at a time.
 * Remounts / refreshes the shared Splide driver when a panel becomes visible.
 */
(() => {
  const TAG = 'about-this-product';

  if (customElements.get(TAG)) return;

  class AboutThisProduct extends HTMLElement {
    #triggers = [];
    #panels = [];
    #activeId = null;

    connectedCallback() {
      this.#triggers = Array.from(this.querySelectorAll('[data-about-tab-trigger]'));
      this.#panels = Array.from(this.querySelectorAll('[data-about-tab-panel]'));

      if (this.#triggers.length === 0 || this.#panels.length === 0) return;

      for (const trigger of this.#triggers) {
        trigger.addEventListener('click', this.#handleTriggerClick);
        trigger.addEventListener('keydown', this.#handleKeydown);
      }

      const initial =
        this.#triggers.find((trigger) => trigger.getAttribute('aria-selected') === 'true') ||
        this.#triggers[0];

      this.#activate(initial.getAttribute('data-about-tab-trigger'), { focus: false });
    }

    disconnectedCallback() {
      for (const trigger of this.#triggers) {
        trigger.removeEventListener('click', this.#handleTriggerClick);
        trigger.removeEventListener('keydown', this.#handleKeydown);
      }
    }

    #handleTriggerClick = (event) => {
      const trigger = event.currentTarget;
      const id = trigger.getAttribute('data-about-tab-trigger');
      if (!id || id === this.#activeId) return;
      this.#activate(id, { focus: false });
    };

    #handleKeydown = (event) => {
      const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (!keys.includes(event.key)) return;

      event.preventDefault();

      const index = this.#triggers.indexOf(event.currentTarget);
      if (index < 0) return;

      let nextIndex = index;

      if (event.key === 'ArrowRight') {
        nextIndex = (index + 1) % this.#triggers.length;
      } else if (event.key === 'ArrowLeft') {
        nextIndex = (index - 1 + this.#triggers.length) % this.#triggers.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this.#triggers.length - 1;
      }

      const next = this.#triggers[nextIndex];
      this.#activate(next.getAttribute('data-about-tab-trigger'), { focus: true });
    };

    #activate(id, { focus = false } = {}) {
      if (!id) return;

      this.#activeId = id;

      for (const trigger of this.#triggers) {
        const isActive = trigger.getAttribute('data-about-tab-trigger') === id;
        trigger.classList.toggle('is-active', isActive);
        trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
        trigger.setAttribute('tabindex', isActive ? '0' : '-1');

        if (isActive && focus) {
          trigger.focus();
        }
      }

      for (const panel of this.#panels) {
        const isActive = panel.getAttribute('data-about-tab-panel') === id;
        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);

        if (isActive) {
          this.#refreshCarousel(panel);
        }
      }
    }

    #refreshCarousel(panel) {
      const carousel = panel.querySelector('.splide[data-carousel]');
      if (!carousel) return;

      // Wait a frame so the panel is laid out after removing [hidden], then
      // evaluate only this carousel. A global resize would run evaluateAll —
      // including display:none siblings — which can tear them down via fitsTrack
      // and force a remount (+ 200ms debounce) on every tab revisit.
      requestAnimationFrame(() => {
        if (window.themeCarousel && typeof window.themeCarousel.evaluate === 'function') {
          window.themeCarousel.evaluate(carousel);
          return;
        }

        if (carousel.__splide) {
          try {
            carousel.__splide.refresh();
          } catch (error) {
            // Splide may already be mid-destroy from a section unload.
          }
        }
      });
    }
  }

  customElements.define(TAG, AboutThisProduct);
})();
